import { useState } from "react"

import { toast } from "sonner"

import {
  useCreateCustomer,
  useCreateLocation,
  useCreateLocationCustomer,
  useCreateSlot,
} from "@/hooks/useCollections"
import { calculateServiceTime, calculateSlotId } from "@/lib/slotIdentity"
import { Customer, Location, Slot } from "@/types/collections"

import { CustomerFormData, LocationFormData, SlotFormData } from "./types"

export const blankSlot = (): Partial<SlotFormData> => ({
  start_time: "08:00",
  end_time: "10:00",
  days: [],
  slot_type: "shipper_collection",
  slot_services: [],
  slot_pnl: [],
  is_active: true,
})

interface BulkCreateRefetch {
  refetchCustomers: () => unknown
  refetchLocations: () => unknown
  refetchSlots: () => unknown
}

export function useBulkCreate(
  locations: Location[],
  { refetchCustomers, refetchLocations, refetchSlots }: BulkCreateRefetch,
) {
  const createCustomer = useCreateCustomer()
  const createLocation = useCreateLocation()
  const createLocationCustomer = useCreateLocationCustomer()
  const createSlot = useCreateSlot()

  const [open, setOpen] = useState(false)
  const [includeLocation, setIncludeLocation] = useState(true)
  const [includeSlots, setIncludeSlots] = useState(false)
  const [customerForm, setCustomerForm] = useState<Partial<CustomerFormData>>({
    customer_type: "shipper",
    status: "active",
  })
  const [locationForm, setLocationForm] = useState<Partial<LocationFormData>>({
    status: "active",
  })
  const [slotForms, setSlotForms] = useState<Partial<SlotFormData>[]>([blankSlot()])
  const [samsaraFailed, setSamsaraFailed] = useState(false)

  const openDialog = () => {
    setCustomerForm({
      name: "",
      account_id: "",
      customer_type: "shipper",
      status: "active",
    })
    setLocationForm({
      site_name: "",
      location_id: "",
      internal_id: "",
      address: "",
      postcode: "",
      latitude: null,
      longitude: null,
      status: "active",
    })
    setSlotForms([blankSlot()])
    setIncludeLocation(true)
    setIncludeSlots(false)
    setSamsaraFailed(false)
    setOpen(true)
  }

  const create = async () => {
    try {
      // Created in sequence, because each row needs the id of the one before it.
      if (includeLocation) {
        const customerResult = await createCustomer.mutateAsync({
          name: customerForm.name || "",
          account_id: customerForm.account_id || "",
          customer_type: customerForm.customer_type || "shipper",
          status: customerForm.status || "active",
        } as Customer)

        if (customerResult) {
          const locationResult = await createLocation.mutateAsync({
            site_name: locationForm.site_name || "",
            location_id: locationForm.location_id || "",
            internal_id: locationForm.internal_id,
            contact_name: locationForm.contact_name || undefined,
            contact_phone: locationForm.contact_phone || undefined,
            customer_id: customerResult.id,
            address: locationForm.address || "",
            postcode: locationForm.postcode || "",
            latitude: locationForm.latitude ?? 0,
            longitude: locationForm.longitude ?? 0,
            collection_protocol: locationForm.collection_protocol,
            opening_time: locationForm.opening_time || undefined,
            closing_time: locationForm.closing_time || undefined,
            loading_unit_type: locationForm.loading_unit_type || undefined,
            parcels_per_loading_unit: locationForm.parcels_per_loading_unit || undefined,
            status: locationForm.status || "active",
          } as Location)

          // Write the junction row too, so bulk-created sites match single-dialog ones.
          if (locationResult) {
            await createLocationCustomer.mutateAsync({
              location_id: locationResult.id,
              customer_id: customerResult.id,
              is_primary: false,
              relationship_type: "owner",
            })
          }

          if (includeSlots && slotForms.length > 0 && locationResult) {
            for (const slotForm of slotForms) {
              const days = slotForm.days || []
              const serviceTime =
                slotForm.start_time && slotForm.end_time
                  ? calculateServiceTime(slotForm.start_time, slotForm.end_time)
                  : 0
              const slotName = calculateSlotId(
                locationResult.id,
                locations.find((l) => l.id === locationResult.id),
                days,
                slotForm.start_time || "08:00",
              )

              await createSlot.mutateAsync({
                slot_name: slotName,
                location_id: locationResult.id,
                start_time: slotForm.start_time || "08:00",
                end_time: slotForm.end_time || "10:00",
                service_time_minutes: serviceTime,
                days: days,
                slot_type: slotForm.slot_type || "shipper_collection",
                slot_services: slotForm.slot_services || [],
                slot_pnl: slotForm.slot_pnl || [],
                is_active: slotForm.is_active ?? true,
              } as unknown as Slot)
            }
          }
        }

        toast.success("Bulk creation successful")
      } else {
        await createCustomer.mutateAsync({
          name: customerForm.name || "",
          account_id: customerForm.account_id || "",
          customer_type: customerForm.customer_type || "shipper",
          status: customerForm.status || "active",
        } as Customer)
        toast.success("Customer created successfully")
      }

      setOpen(false)
      refetchCustomers()
      refetchLocations()
      refetchSlots()
    } catch (error) {
      toast.error("Error during bulk creation", {
        description: error instanceof Error ? error.message : "Unknown error",
      })
    }
  }

  const isFormValid = () => {
    const customerValid = customerForm.name && customerForm.account_id

    if (!includeLocation) {
      return customerValid
    }

    const hasSamsaraLocation =
      !!locationForm.location_id &&
      locationForm.latitude != null &&
      locationForm.longitude != null
    const locationValid =
      locationForm.site_name &&
      locationForm.address &&
      locationForm.postcode &&
      hasSamsaraLocation

    if (!includeSlots) {
      return customerValid && locationValid
    }

    if (slotForms.length === 0) {
      return false
    }

    const allSlotsValid = slotForms.every((slotForm) => {
      const days = slotForm.days || []
      return days.length > 0 && slotForm.start_time && slotForm.end_time
    })

    return customerValid && locationValid && allSlotsValid
  }

  const isSaving = createCustomer.isPending || createLocation.isPending || createSlot.isPending

  return {
    open,
    setOpen,
    includeLocation,
    setIncludeLocation,
    includeSlots,
    setIncludeSlots,
    customerForm,
    setCustomerForm,
    locationForm,
    setLocationForm,
    slotForms,
    setSlotForms,
    samsaraFailed,
    setSamsaraFailed,
    openDialog,
    create,
    isFormValid,
    isSaving,
  }
}

export type BulkCreateState = ReturnType<typeof useBulkCreate>
